import {Connection, Edge} from 'reactflow';

import notifications from 'notifications';
import {getEdgeTypeFromHandleId} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/flowchartIdentifierUtils';
import {ProcessFlowchartEdgeType} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {warn} from 'util/Logging';

const NOTIFICATION_OPTIONS = {duration: 5};

/**
 * Checks if the connection has valid source and target handles.
 *
 * @param {Connection} connection - The connection to validate.
 * @returns {boolean} - Returns `true` if both source and target handles are defined, otherwise `false`.
 */
function hasValidHandles(connection: Connection): boolean {
  return connection.sourceHandle != null && connection.targetHandle != null;
}

/**
 * Checks if the edge types of the source and target handles match.
 *
 * Assumes source and target handles on the connection are defined.
 *
 * @param {Connection} connection - The connection to validate.
 * @returns {boolean} - Returns `true` if the edge types match, otherwise `false`.
 */
function hasMatchingEdgeTypes(connection: Connection): boolean {
  const sourceHandleType = getEdgeTypeFromHandleId(connection.sourceHandle!);
  const targetHandleType = getEdgeTypeFromHandleId(connection.targetHandle!);

  const handleTypesMatch = sourceHandleType === targetHandleType;

  if (!handleTypesMatch) {
    switch (sourceHandleType) {
      case ProcessFlowchartEdgeType.DATA_FLOW:
        notifications.warn(
          "You can't connect a data field to a node, only another data field.",
          NOTIFICATION_OPTIONS
        );
        break;
      case ProcessFlowchartEdgeType.CONTROL_FLOW:
        notifications.warn(
          "You can't connect a node to a data field, only another node.",
          NOTIFICATION_OPTIONS
        );
        break;
      default:
        notifications.warn("You can't connect these handles.", NOTIFICATION_OPTIONS);
        warn(`Unknown handle type: ${sourceHandleType}`);
        break;
    }
  }

  return handleTypesMatch;
}

/**
 * Checks if the edge is not a duplicate of existing edges.
 *
 * Assumes source and target handles on the connection are defined.
 *
 * @param {Connection} connection - The connection to validate.
 * @param {Array<Edge>} existingEdges - The list of existing edges in the flowchart.
 * @returns {boolean} - Returns `true` if the edge is not a duplicate, otherwise `false`.
 */
function isEdgeNotDuplicate(connection: Connection, existingEdges: Array<Edge>): boolean {
  return !existingEdges.some(
    (edge) =>
      edge.source === connection.source &&
      edge.target === connection.target &&
      edge.sourceHandle === connection.sourceHandle &&
      edge.targetHandle === connection.targetHandle
  );
}

/**
 * Checks if a new data flow edge targets a field that already has a data flow edge.
 *
 * Assumes target handle on the connection is defined.
 *
 * @param {Connection} connection - The connection to validate.
 * @param {Array<Edge>} existingEdges - The list of existing edges in the flowchart.
 * @returns {boolean} - Returns `true` if the new data flow edge is valid, otherwise `false`.
 */
function isNewDataEdgeForTarget(connection: Connection, existingEdges: Array<Edge>): boolean {
  const isNewDataEdgeForTarget = !existingEdges.some(
    (edge) =>
      edge.target === connection.target &&
      edge.targetHandle === connection.targetHandle &&
      edge.type === ProcessFlowchartEdgeType.DATA_FLOW
  );
  if (!isNewDataEdgeForTarget) {
    notifications.warn('A data flow edge already targets this field.', NOTIFICATION_OPTIONS);
  }
  return isNewDataEdgeForTarget;
}

/**
 * Checks if a new control flow edge does not originate from a node that is already the target of another
 * edge.
 *
 * This prevents defining more than one transition in the transition modal.
 *
 * @param {Connection} connection - The connection to validate.
 * @param {Array<Edge>} existingEdges - The list of existing edges in the flowchart.
 * @returns {boolean} - Returns `true` if the new control flow edge is valid, otherwise `false`.
 */
function isNotSourceAlreadyTarget(connection: Connection, existingEdges: Array<Edge>): boolean {
  const isNotSourceAlreadyTarget = !existingEdges.some(
    (edge) => edge.target === connection.source && edge.type === ProcessFlowchartEdgeType.CONTROL_FLOW
  );
  if (!isNotSourceAlreadyTarget) {
    notifications.warn(
      "You can't connect a destination node to another node downstream of it.",
      NOTIFICATION_OPTIONS
    );
  }
  return isNotSourceAlreadyTarget;
}

/**
 * Checks if a new control flow edge does not target a node that is already the source of another
 * edge.
 *
 * This prevents defining more than one transition in the transition modal.
 *
 * @param {Connection} connection - The connection to validate.
 * @param {Array<Edge>} existingEdges - The list of existing edges in the flowchart.
 * @returns {boolean} - Returns `true` if the new control flow edge is valid, otherwise `false`.
 */
function isNotTargetAlreadySource(connection: Connection, existingEdges: Array<Edge>): boolean {
  const isNotTargetAlreadySource = !existingEdges.some(
    (edge) => edge.source === connection.target && edge.type === ProcessFlowchartEdgeType.CONTROL_FLOW
  );
  if (!isNotTargetAlreadySource) {
    notifications.warn(
      "You can't connect a source node to a previous node in a transition.",
      NOTIFICATION_OPTIONS
    );
  }
  return isNotTargetAlreadySource;
}

/**
 * Checks if a new control flow edge does not originate from a node that is already the source of another
 * edge.
 *
 * This prevents defining more than one transition in the transition modal.
 *
 * @param {Connection} connection - The connection to validate.
 * @param {Array<Edge>} existingEdges - The list of existing edges in the flowchart.
 * @returns {boolean} - Returns `true` if the new control flow edge is valid, otherwise `false`.
 */
function isNotMultipleSourceEdges(connection: Connection, existingEdges: Array<Edge>): boolean {
  const isNotMultipleSourceEdges = !existingEdges.some(
    (edge) =>
      edge.source === connection.source &&
      edge.sourceHandle === connection.sourceHandle &&
      edge.type === ProcessFlowchartEdgeType.CONTROL_FLOW
  );
  if (!isNotMultipleSourceEdges) {
    notifications.warn("You can't draw more than one edge from a source node.", NOTIFICATION_OPTIONS);
  }
  return isNotMultipleSourceEdges;
}

/**
 * Validates whether a new edge can be added to the flowchart in a transition.
 *
 * @param {Connection} connection - The proposed connection, including source and target handles.
 * @param {Array<Edge>} existingEdges - The list of existing edges in the flowchart.
 * @returns {boolean} - Returns `true` if the new edge is valid, otherwise `false`.
 *
 * Validation checks:
 * 1. Ensures both source and target handles are defined.
 * 2. Ensures the edge types of the source and target handles match.
 * 3. Ensures the edge does not already exist in the flowchart.
 * 4. Ensures no other data flow edge targets the same field.
 * 5. Prevents connecting a destination node to another node downstream of it.
 * 6. Prevents connecting a source node to a previous node in a transition.
 * 7. Prevents drawing more than one control flow edge from the same source node.
 */
export function isNewEdgeValidForTransition(connection: Connection, existingEdges: Array<Edge>): boolean {
  return (
    hasValidHandles(connection) &&
    hasMatchingEdgeTypes(connection) &&
    isEdgeNotDuplicate(connection, existingEdges) &&
    isNewDataEdgeForTarget(connection, existingEdges) &&
    isNotSourceAlreadyTarget(connection, existingEdges) &&
    isNotTargetAlreadySource(connection, existingEdges) &&
    isNotMultipleSourceEdges(connection, existingEdges)
  );
}

/**
 * Validates whether a new edge can be added to the flowchart.
 *
 * @param {Connection} connection - The proposed connection, including source and target handles.
 * @param {Array<Edge>} existingEdges - The list of existing edges in the flowchart.
 * @returns {boolean} - Returns `true` if the new edge is valid, otherwise `false`.
 *
 * Validation checks:
 * 1. Ensures both source and target handles are defined.
 * 2. Ensures the edge types of the source and target handles match.
 * 3. Ensures the edge does not already exist in the flowchart.
 */
export function isNewEdgeValidForFlowchart(connection: Connection, existingEdges: Array<Edge>): boolean {
  return (
    hasValidHandles(connection) &&
    hasMatchingEdgeTypes(connection) &&
    isEdgeNotDuplicate(connection, existingEdges)
  );
}
